import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  applyChamberTheme,
  clearChamberTheme,
} from '@provenance/design';
import {
  SESSION_IDLE_MINUTES,
  SESSION_IDLE_WARNING_SECONDS,
  type LoginResponse,
  type SessionUser,
} from '@provenance/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ACCESS_KEY, REFRESH_KEY, api, setUnauthenticatedHandler, subjectOf, tokens } from './api';
import { landingFor, rememberSignedOutReason } from './landing';
import { setSupportChamber, supportChamber, type SupportChamber } from './support';

/**
 * A support officer inside a chamber is presented as belonging to it for this tab (D-031), so
 * the sidebar, branding and every screen work unchanged. The server enforces the real rule.
 */
function withSupportChamber(user: SessionUser): SessionUser {
  if (user.role !== 'PLATFORM_SUPPORT') return user;
  const chamber = supportChamber();
  if (!chamber) return { ...user, chamberId: null, chamber: null };
  return {
    ...user,
    chamberId: chamber.id,
    chamber: {
      id: chamber.id,
      slug: chamber.slug,
      legalName: chamber.legalName,
      shortName: chamber.shortName,
      logoUrl: chamber.logoUrl,
      primaryColor: chamber.primaryColor,
      secondaryColor: chamber.secondaryColor,
    },
  };
}

/** Last keyboard, mouse or touch activity in ANY tab, so one busy tab keeps the others alive. */
const ACTIVITY_KEY = 'pv.activity';
const IDLE_MS = SESSION_IDLE_MINUTES * 60_000;

function markActive(): void {
  try {
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
  } catch {
    /* storage blocked: idle tracking then covers this tab only */
  }
}

function lastActive(fallback: number): number {
  try {
    return Math.max(fallback, Number(localStorage.getItem(ACTIVITY_KEY)) || 0);
  } catch {
    return fallback;
  }
}

/** Ends the page, not just the state: nothing of the previous session survives a reload. */
function reloadSignedOut(): void {
  window.location.replace('/');
}

/**
 * Session and chamber theming.
 *
 * The theme is applied here, at the same moment the user is known, because a console
 * that renders unbranded and then repaints a chamber's colour a beat later looks broken.
 * The chamber's branding therefore rides along in the login and /auth/me responses
 * rather than being a separate request.
 */
interface SessionContextValue {
  user: SessionUser | null;
  loading: boolean;
  /** Set when a chamber's brand colour cannot carry legible text. */
  themeWarning: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  /** Where to send someone who has just signed in; consumed once (T-140). */
  landing: string | null;
  consumeLanding: () => void;
  /** Support only (D-031): work in this chamber, or choose another. Reloads the page. */
  /** `to`: where to land once in the chamber (default the dashboard). */
  chooseChamber: (chamber: SupportChamber | null, to?: string) => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [themeWarning, setThemeWarning] = useState<string | null>(null);
  const [landing, setLanding] = useState<string | null>(null);
  const [idleSecondsLeft, setIdleSecondsLeft] = useState<number | null>(null);
  const mounted = useRef(true);
  const signedIn = useRef(false);
  useEffect(() => {
    signedIn.current = user !== null;
  }, [user]);
  const queryClient = useQueryClient();

  const applyTheme = useCallback((next: SessionUser | null) => {
    if (!next?.chamber) {
      clearChamberTheme();
      setThemeWarning(null);
      return;
    }
    const applied = applyChamberTheme({
      primaryColor: next.chamber.primaryColor,
      secondaryColor: next.chamber.secondaryColor,
    });
    // A chamber whose brand is mid-yellow would otherwise get invisible buttons. The
    // theme falls back for text surfaces and says so, rather than failing silently.
    setThemeWarning(applied.degraded ? (applied.reason ?? null) : null);
  }, []);

  const loadUser = useCallback(async () => {
    if (!tokens.access) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = withSupportChamber(await api.get<SessionUser>('/api/console/auth/me'));
      if (!mounted.current) return;
      setUser(me);
      applyTheme(me);
    } catch {
      tokens.clear();
      if (mounted.current) setUser(null);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [applyTheme]);

  useEffect(() => {
    mounted.current = true;
    setUnauthenticatedHandler(() => {
      // Same reasoning as signOut: a session ending because the token expired must not
      // leave the previous user's rows sitting in the cache for whoever signs in next.
      queryClient.clear();
      clearChamberTheme();
      // Ended while someone was using it (time limit, revoked, signed out elsewhere): reload
      // at `/`, as signOut does. Rendering the sign-in screen in place kept their URL, and the
      // next person to sign in landed on it (T-140). During the first load there is no
      // previous screen, and the URL is a link someone followed — keep it.
      if (signedIn.current) {
        rememberSignedOutReason('ended');
        reloadSignedOut();
        return;
      }
      setUser(null);
    });
    void loadUser();
    return () => {
      mounted.current = false;
    };
    // `queryClient` is a stable instance from the provider, so listing it changes nothing
    // at runtime — but leaving it out would mean the handler closed over a stale one if
    // that ever stopped being true.
  }, [loadUser, queryClient]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const res = await api.anon<LoginResponse>('/api/console/auth/login', { email, password });
      // Before anything renders for the new identity. A cache keyed by query name alone
      // carries no notion of who fetched it, so without this a second person signing in
      // at the same browser is shown whatever the first one had loaded.
      queryClient.clear();
      // A chamber chosen by whoever used this tab before is not this person's choice.
      setSupportChamber(null);
      tokens.set(res.accessToken, res.refreshToken);
      // A fresh session starts active; a stale timestamp from the last one would sign it out.
      markActive();
      setLanding(landingFor(res.user.role));
      setUser(res.user);
      applyTheme(res.user);
    },
    [applyTheme, queryClient],
  );

  /**
   * Sign out completely.
   *
   * Clearing the tokens is not enough on its own, and used not to be: the query cache kept
   * every row the previous user had loaded, and the URL kept pointing at the screen they
   * were on. Signing back in as someone else then rendered that screen from cache — so a
   * chamber admin could be shown the platform chamber list a platform admin had just been
   * looking at. The API refused to *serve* it to them (403), but they could still read it.
   *
   * So this ends with a **full page load**, not a client-side navigation. It is the only
   * way to guarantee that nothing survives: no query cache, no component state, no closure
   * still holding a response. On a machine two people share, that guarantee is worth more
   * than a smooth transition.
   */
  const signOut = useCallback(async () => {
    const refreshToken = tokens.refresh;
    tokens.clear();
    setSupportChamber(null);
    queryClient.clear();
    setUser(null);
    clearChamberTheme();
    // Best-effort: the local session is already gone, so a failed server-side revoke
    // must not leave the user stuck on a screen they have logged out of.
    if (refreshToken) {
      await api.anon('/api/console/auth/logout', { refreshToken }).catch(() => undefined);
    }
    // `replace`, so Back does not return to the screen they just left.
    window.location.replace('/');
  }, [queryClient]);

  // ── Tabs follow each other (T-140) ─────────────────────────────────────────
  // Tabs share one session in localStorage. When another tab signs out, or signs in as
  // someone else, this one must not keep showing the old screens — and it would otherwise
  // send their requests with the new person's token.
  useEffect(() => {
    if (!user) return;
    const onStorage = (event: StorageEvent) => {
      if (event.key === REFRESH_KEY && event.newValue === null) {
        rememberSignedOutReason('elsewhere');
        reloadSignedOut();
      } else if (event.key === ACCESS_KEY && event.newValue && subjectOf(event.newValue) !== user.id) {
        reloadSignedOut();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [user]);

  // ── Idle sign-out (T-140) ──────────────────────────────────────────────────
  // Background polling refreshes the session, so without this an open tab on an unattended
  // desk stayed signed in for good. Only a person's input counts.
  const lastLocal = useRef(Date.now());
  useEffect(() => {
    if (!user) return;
    let lastWrite = 0;
    const onActivity = () => {
      lastLocal.current = Date.now();
      // Throttled: a keystroke per write would be needless storage churn.
      if (lastLocal.current - lastWrite > 5_000) {
        lastWrite = lastLocal.current;
        markActive();
      }
    };
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;
    for (const name of events) window.addEventListener(name, onActivity, { passive: true });

    const tick = () => {
      const left = IDLE_MS - (Date.now() - lastActive(lastLocal.current));
      if (left <= 0) {
        rememberSignedOutReason('idle');
        void signOut();
      } else {
        setIdleSecondsLeft(left <= SESSION_IDLE_WARNING_SECONDS * 1_000 ? Math.ceil(left / 1_000) : null);
      }
    };
    tick();
    const timer = window.setInterval(tick, 1_000);
    return () => {
      for (const name of events) window.removeEventListener(name, onActivity);
      window.clearInterval(timer);
    };
  }, [user, signOut]);

  const consumeLanding = useCallback(() => setLanding(null), []);

  // Switching chamber reloads the page: nothing loaded in one chamber may linger in the next.
  const chooseChamber = useCallback((chamber: SupportChamber | null, to?: string) => {
    setSupportChamber(chamber);
    window.location.assign(chamber ? (to ?? '/') : '/support');
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({ user, loading, themeWarning, signIn, signOut, refreshUser: loadUser, landing, consumeLanding, chooseChamber }),
    [user, loading, themeWarning, signIn, signOut, loadUser, landing, consumeLanding, chooseChamber],
  );

  return (
    <SessionContext.Provider value={value}>
      {children}
      <Dialog
        open={idleSecondsLeft !== null}
        onOpenChange={(open) => {
          if (!open) {
            markActive();
            setIdleSecondsLeft(null);
          }
        }}
      >
        <DialogContent size="sm">
          <DialogHeader
            title="Are you still there?"
            description="You have not used Provenir for a while."
          />
          <DialogBody>
            <p className="[font-size:14px]" aria-live="polite">
              To keep certificates safe on a shared or unattended computer, you will be signed out in{' '}
              <strong className="pv-num">{formatCountdown(idleSecondsLeft ?? 0)}</strong>.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => void signOut()}>
              Sign out now
            </Button>
            <Button
              onClick={() => {
                markActive();
                setIdleSecondsLeft(null);
              }}
            >
              Stay signed in
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SessionContext.Provider>
  );
}

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
